import api, { unwrap } from './client.js';

/**
 * One function per endpoint, grouped by resource.
 *
 * Thin on purpose: each unwraps the API envelope and returns the payload.
 * Nothing here shapes, filters or derives — that belongs to the screen that
 * asked, and a transform hidden in the fetch layer is one nobody finds when the
 * number on the page is wrong.
 *
 * Query objects are cleaned before they go out. An empty string in a query
 * string is not the same as the parameter being absent: `?status=` reaches the
 * validator as a value and, depending on the rule, either fails validation or
 * filters on the empty string. Stripping empties here means a cleared filter in
 * the UI behaves like a filter that was never set.
 */

/** Drops keys whose value is empty, so a cleared filter disappears entirely. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

const get = (url, params) => api.get(url, { params: clean(params) }).then(unwrap);

/* -------------------------------------------------------------------------- */
/* Session                                                                    */
/* -------------------------------------------------------------------------- */

export const authApi = {
  /** Exchanges credentials for an access token and a refresh cookie. */
  login: (credentials) =>
    api
      .post('/admin/auth/login', credentials, {
        // A 401 here means the credentials were wrong, not that the session
        // lapsed — running the refresh interceptor on it would fire a pointless
        // refresh and could surface a confusing "session revoked" toast on what
        // is simply a typo.
        skipAuthRefresh: true,
      })
      .then(unwrap),

  logout: (allDevices = false) => api.post('/admin/auth/logout', { allDevices }).then(unwrap),

  me: () => api.get('/admin/auth/me').then(unwrap),

  /** Succeeds and ends every session, including the one that called it. */
  changePassword: (payload) => api.patch('/admin/auth/password', payload).then(unwrap),
};

/* -------------------------------------------------------------------------- */
/* Dashboard                                                                  */
/* -------------------------------------------------------------------------- */

export const statsApi = {
  overview: (params) => get('/admin/stats', params),
  timeseries: (params) => get('/admin/stats/timeseries', params),
};

/* -------------------------------------------------------------------------- */
/* Users                                                                      */
/* -------------------------------------------------------------------------- */

export const usersApi = {
  list: (params) => get('/admin/users', params),
  detail: (id) => get(`/admin/users/${id}`),
  overview: (id) => get(`/admin/users/${id}/overview`),
  team: (id, params) => get(`/admin/users/${id}/team`, params),

  update: (id, payload) => api.patch(`/admin/users/${id}`, payload).then(unwrap),

  /** Soft delete by default; `permanent` needs a super admin and has no undo. */
  remove: (id, { permanent = false } = {}) =>
    api.delete(`/admin/users/${id}`, { params: permanent ? { permanent: true } : {} }).then(unwrap),

  restore: (id) => api.patch(`/admin/users/${id}/restore`).then(unwrap),

  /**
   * Gates the account's dynamic income claim — referral, rank and upline
   * income — and nothing else. Staking withdrawals are a separate path and are
   * unaffected.
   *
   * Any operator may call it; there is no super admin requirement. It takes
   * effect on the account's very next request, since nothing about this gate is
   * cached server-side.
   */
  setDynamicWithdraw: (id, enabled) =>
    api.patch(`/admin/users/${id}/dynamic-withdraw`, { enabled }).then(unwrap),

  /**
   * Sets `withdrawEnabled` on the record and audits it. Any operator may call
   * it, same as the dynamic-claim gate above.
   *
   * Unlike that gate, this one is a flag, not an enforcement point yet:
   * nothing in the withdrawal request path currently reads it to refuse a
   * request. Flip the panel copy once withdrawal.service.js actually checks it.
   */
  setWithdrawEnabled: (id, enabled) =>
    api.patch(`/admin/users/${id}/withdraw`, { enabled }).then(unwrap),

  setTokenSellEnabled: (id, enabled) =>
    api.patch(`/admin/users/${id}/token-sell`, { enabled }).then(unwrap),

  bulkSetTokenSellEnabled: (userIds, enabled) =>
    api.patch('/admin/users/bulk/token-sell', { userIds, enabled }).then(unwrap),

  /** Mints a live access token for the account, on the calling super admin's behalf. */
  impersonate: (id) => api.post(`/admin/users/${id}/impersonate`).then(unwrap),
};

/* -------------------------------------------------------------------------- */
/* Stakes                                                                     */
/* -------------------------------------------------------------------------- */

export const stakesApi = {
  list: (params) => get('/admin/stakes', params),
  detail: (id) => get(`/admin/stakes/${id}`),
};

/* -------------------------------------------------------------------------- */
/* Withdrawals — read-only                                                    */
/* -------------------------------------------------------------------------- */

/**
 * No `approve`, `reject` or `cancel`, and none can be added: the API has no
 * such endpoints. A withdrawal row is an EIP-712 signature already handed to
 * the client, which the contract will honour until its deadline — releasing the
 * amount here while that signature is live would authorise the same income
 * twice. Only the deadline expiring releases one.
 */
export const withdrawalsApi = {
  list: (params) => get('/admin/withdrawals', params),
  detail: (id) => get(`/admin/withdrawals/${id}`),
};

/* -------------------------------------------------------------------------- */
/* Income — read-only                                                         */
/* -------------------------------------------------------------------------- */

/** Append-only ledgers. Nothing credits, debits or reverses a row. */
export const incomeApi = {
  list: (stream, params) => get(`/admin/income/${stream}`, params),
  rankHistory: (params) => get('/admin/rank-history', params),
};

/* -------------------------------------------------------------------------- */
/* Offer claims — fulfillment                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The reward tiers themselves (title, reward, qualification, numberOfAchievers)
 * have no admin API — they are edited in `offer.seeder.js` and re-seeded. This
 * is the claim queue only: moving a claim through claimed → processing →
 * delivered.
 */
export const offersApi = {
  achievements: {
    list: (params) => get('/admin/offer-achievements', params),
    detail: (id) => get(`/admin/offer-achievements/${id}`),

    /** CAS from `claimed`. The API answers 409 if the claim has moved on. */
    markProcessing: (id, payload) =>
      api.patch(`/admin/offer-achievements/${id}/processing`, payload).then(unwrap),

    /** CAS from `processing` — there is no skip-straight-to-delivered. */
    markDelivered: (id, payload) =>
      api.patch(`/admin/offer-achievements/${id}/delivered`, payload).then(unwrap),
  },
};

/* -------------------------------------------------------------------------- */
/* Registration reward claims — fulfillment                                   */
/* -------------------------------------------------------------------------- */

/**
 * The registration reward fulfillment queue — same shape as offer claims:
 * claimed → processing → delivered, one claim at a time, no bulk action. Any
 * authenticated operator may work this queue, with no super_admin gate.
 */
export const registrationRewardsApi = {
  list: (params) => get('/admin/registration-rewards', params),
  detail: (id) => get(`/admin/registration-rewards/${id}`),

  /** CAS from `claimed`. The API answers 409 if the claim has moved on. */
  markProcessing: (id, payload) =>
    api.patch(`/admin/registration-rewards/${id}/processing`, payload).then(unwrap),

  /** CAS from `processing` — there is no skip-straight-to-delivered. */
  markDelivered: (id, payload) =>
    api.patch(`/admin/registration-rewards/${id}/delivered`, payload).then(unwrap),
};

/* -------------------------------------------------------------------------- */
/* Reference data — super_admin writes                                        */
/* -------------------------------------------------------------------------- */

export const packagesApi = {
  list: (params) => get('/admin/packages', params),
  create: (payload) => api.post('/admin/packages', payload).then(unwrap),
  /** Activation only — the API refuses a term change with an explanation. */
  update: (pkgNum, payload) => api.patch(`/admin/packages/${pkgNum}`, payload).then(unwrap),
};

export const ranksApi = {
  list: (params) => get('/admin/ranks', params),
  create: (payload) => api.post('/admin/ranks', payload).then(unwrap),
  update: (rank, payload) => api.patch(`/admin/ranks/${rank}`, payload).then(unwrap),
};

export const uplineLevelsApi = {
  list: (params) => get('/admin/upline-levels', params),
  create: (payload) => api.post('/admin/upline-levels', payload).then(unwrap),
  update: (level, payload) => api.patch(`/admin/upline-levels/${level}`, payload).then(unwrap),
};

export const settingsApi = {
  get: () => get('/admin/settings'),
  /** An address change needs `confirm: true` or the API answers 400. */
  update: (payload) => api.patch('/admin/settings', payload).then(unwrap),
};

/* -------------------------------------------------------------------------- */
/* Operations                                                                 */
/* -------------------------------------------------------------------------- */

export const auditApi = {
  list: (params) => get('/admin/audit-logs', params),
};

export const systemApi = {
  jobs: () => get('/admin/jobs'),

  /**
   * The health probe.
   *
   * Sent with the operator's token because the payload is tiered — an
   * unauthenticated probe gets status and uptime, an authenticated one gets the
   * dependency and system detail the System screen renders.
   */
  health: () => get('/health'),
};

/**
 * Platform-wide kill switches, one per user-facing action. Unlike the
 * per-user withdraw flag on `usersApi`, these are enforced today: flipping
 * one off makes every account's next request for that action fail with a
 * generic "temporarily unavailable" message — worded identically to a real
 * outage so it is never visible to a user as an admin toggle.
 *
 * `swap` and `collect` are accepted and stored but currently gate nothing —
 * neither action exists yet on the API.
 */
export const actionTogglesApi = {
  list: () => get('/admin/action-toggles'),

  /** `action`: withdrawal | swap | staking | claimRoi | claimDynamicWithdrawal | collect | unstake */
  set: (action, enabled) => api.patch(`/admin/action-toggles/${action}`, { enabled }).then(unwrap),
};

export default {
  authApi,
  statsApi,
  usersApi,
  stakesApi,
  withdrawalsApi,
  incomeApi,
  offersApi,
  registrationRewardsApi,
  packagesApi,
  ranksApi,
  uplineLevelsApi,
  settingsApi,
  auditApi,
  systemApi,
  actionTogglesApi,
};
